import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProductViewModel } from '../viewmodels/useProductsViewModel';
import { useCartViewModel } from '../viewmodels/useCartViewModel';
import { useTheme } from '../themes/ThemeProvider';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Icon } from '../components/checkout/icons';
import RelatedProducts from '../components/product/RelatedProducts';
import ProductInfoTabs, { type ProductInfoTab } from '../components/product/ProductInfoTabs';

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { addItem } = useCartViewModel();
  const { branding } = useTheme();
  const showBreadcrumbs = branding?.productDetail?.showBreadcrumbs !== false;
  const showSocialShare = branding?.productDetail?.showSocialShare === true;
  const relatedConfig = branding?.productDetail?.relatedProducts;
  const galleryVariant = branding?.productDetail?.imageGallery?.variant || 'thumbnails-bottom';
  const galleryCursor = branding?.productDetail?.imageGallery?.cursor;
  const thumbsLeft = galleryVariant === 'thumbnails-left';
  // PDP layout strategy. Defaults preserve the existing two-column-equal +
  // stacked layout for any store omitting these keys; a store opts into
  // sidebar-right + tabbed info purely through its branding config.
  const pdpLayout = branding?.productDetail?.layout || 'two-column-equal';
  const pdpInfoLayout = branding?.productDetail?.infoLayout || 'stacked';
  const isSidebarRight = pdpLayout === 'sidebar-right';
  const useInfoTabs = pdpInfoLayout === 'tabs';
  
  const {
    product,
    variants,
    defaultVariant,
    priceRange,
    isLoading,
    error,
  } = useProductViewModel(productId || '');

  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center">
        <h1 className="text-error mb-4">Product Not Found</h1>
        <p className="text-text-secondary mb-8">
          The product you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-primary text-[var(--color-text-on-primary)] border-none px-xl py-md rounded-sm text-base cursor-pointer hover:bg-primary-hover transition-colors"
        >
          Return to Home
        </button>
      </div>
    );
  }

  // Get the current variant (selected or default)
  const currentVariant = selectedVariant
    ? variants.find(v => v.id === selectedVariant)
    : defaultVariant;

  const currentPrice = currentVariant?.calculated_price?.calculated_amount || 0;
  const currency = currentVariant?.calculated_price?.currency_code || 'sek';
  const originalPrice = currentVariant?.calculated_price?.original_amount;
  const isOnSale = typeof originalPrice === 'number' && originalPrice > currentPrice && currentPrice > 0;
  const salePillConfig = branding?.productCard?.salePill;
  const showSalePill = isOnSale && salePillConfig?.enabled !== false;

  const formatPrice = (amount: number, currencyCode: string) => {
    const locale = currencyCode.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
    }).format(amount / 100);
  };

  const formattedPrice = currentPrice > 0 ? formatPrice(currentPrice, currency) : 'Price TBD';
  const formattedOriginalPrice = isOnSale ? formatPrice(originalPrice!, currency) : null;

  const isAvailable = currentVariant && 
    currentVariant.id && 
    (currentVariant.inventory_quantity === undefined || currentVariant.inventory_quantity > 0);

  const handleAddToCart = () => {
    console.log('🛒 Add to Cart clicked:', {
      currentVariant,
      variantId: currentVariant?.id,
      quantity,
      isAvailable,
    });
    
    if (currentVariant && currentVariant.id) {
      console.log('🛒 Calling addItem with variant:', currentVariant.id, 'quantity:', quantity);
      addItem(currentVariant.id, quantity);
    } else {
      console.error('❌ No valid variant found:', { currentVariant, variants, defaultVariant });
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  // --- Composable PDP chunks (issue #143) ---
  // Extracted so the default (two-column-equal + stacked) and the
  // enterprise (sidebar-right + tabs) layouts can compose the same
  // gallery / buy-box / descriptive pieces without duplication.
  const imageGallery = (
    <div className={thumbsLeft ? 'flex gap-4' : ''}>
      {thumbsLeft && product.images && product.images.length > 1 && (
        <div
          className="flex flex-col gap-2 flex-shrink-0"
          role="group"
          aria-label="Product image thumbnails"
          data-testid="pdp-thumbnails-left"
        >
          {product.images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedImageIndex(index)}
              className={`w-20 h-24 overflow-hidden bg-transparent cursor-pointer flex-shrink-0 transition-opacity ${
                selectedImageIndex === index
                  ? 'opacity-100 ring-1 ring-[var(--color-primary)]'
                  : 'opacity-70 hover:opacity-100'
              }`}
              aria-label={`View image ${index + 1}`}
              aria-pressed={selectedImageIndex === index}
            >
              <img
                src={image.url}
                alt={`${product.title} view ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      <div className={thumbsLeft ? 'flex-1 min-w-0' : ''}>
        <div
          className="aspect-[10/11] bg-bg-secondary overflow-hidden mb-4 flex items-center justify-center"
          style={galleryCursor === 'zoom-in' ? { cursor: 'zoom-in' } : undefined}
        >
          {product.images && product.images.length > 0 ? (
            <img
              src={product.images[selectedImageIndex]?.url}
              alt={product.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-text-light flex items-center justify-center" aria-hidden="true">
              <Icon name="shopping-bag" size={64} />
            </span>
          )}
        </div>

        {!thumbsLeft && product.images && product.images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto" role="group" aria-label="Product image thumbnails">
            {product.images.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedImageIndex(index)}
                className={`w-20 h-20 overflow-hidden bg-transparent cursor-pointer flex-shrink-0 ${
                  selectedImageIndex === index ? 'border-2 border-accent' : 'border-2 border-transparent'
                }`}
                aria-label={`View image ${index + 1}`}
                aria-pressed={selectedImageIndex === index}
              >
                <img
                  src={image.url}
                  alt={`${product.title} view ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Buy box = title + subtitle + price + variant + quantity + CTA +
  // availability. Lives in the right column for default, in the sticky
  // sidebar for sidebar-right.
  const buyBox = (
    <>
      <h1 className="text-4xl font-bold text-text-primary mb-4 leading-tight">
        {product.title}
      </h1>

      {product.subtitle && (
        <p className="text-xl text-text-secondary mb-4">
          {product.subtitle}
        </p>
      )}

      {/* Price — shows original-struck + sale pill when the selected
          variant has a Medusa sale price list applied (original_amount >
          calculated_amount). Same gating logic + salePill config as the
          listing's ProductCard so the two stay visually consistent.
          The price-range branch appears when variants differ; the sale
          indicators reference the *selected* variant's price so they
          apply in both cases. */}
      <div className="mb-8" data-testid="product-detail-price">
        <div className="flex flex-wrap items-center gap-3">
          {priceRange && priceRange.min !== priceRange.max ? (
            <span className="text-3xl font-bold text-text-primary" aria-label={`Price range from ${formatPrice(priceRange.min, currency)} to ${formatPrice(priceRange.max, currency)}`}>
              {formatPrice(priceRange.min, currency)} - {formatPrice(priceRange.max, currency)}
            </span>
          ) : (
            <span className="text-3xl font-bold text-text-primary" aria-label={`Price: ${formattedPrice}`}>
              {formattedPrice}
            </span>
          )}
          {isOnSale && formattedOriginalPrice && (
            <span
              className="text-xl text-text-light line-through"
              aria-label={`Original price: ${formattedOriginalPrice}`}
              data-testid="product-detail-original-price"
            >
              {formattedOriginalPrice}
            </span>
          )}
          {showSalePill && (
            <span
              className="text-[0.6875rem] uppercase tracking-wider px-2 py-0.5"
              style={{
                backgroundColor: salePillConfig?.backgroundColor || '#F9DEE5',
                color: salePillConfig?.color || '#AF7B88',
                borderRadius: salePillConfig?.borderRadius || '9999px',
              }}
              data-testid="product-detail-sale-pill"
            >
              {salePillConfig?.text || 'Sale'}
            </span>
          )}
        </div>
      </div>

      {/* Variant Selection */}
      {variants.length > 1 && (
        <div className="mb-8">
          <label htmlFor="variant-select" className="block text-base font-semibold text-text-primary mb-2">
            Variant:
          </label>
          <select
            id="variant-select"
            value={selectedVariant}
            onChange={(e) => setSelectedVariant(e.target.value)}
            className="w-full p-3 border border-border rounded-lg text-base bg-[var(--color-bg)]"
            data-testid="variant-select"
          >
            {variants.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variant.title} - {((variant.calculated_price?.calculated_amount || 0) / 100).toFixed(2)} {variant.calculated_price?.currency_code?.toUpperCase() || 'SEK'}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Quantity Selection */}
      <div className="mb-8">
        <label htmlFor="quantity-input" className="block text-base font-semibold text-text-primary mb-2">
          Quantity:
        </label>
        <div className="flex items-center gap-4" role="group" aria-label="Quantity selector">
          <button
            onClick={() => handleQuantityChange(quantity - 1)}
            disabled={quantity <= 1}
            className="w-10 h-10 border border-border rounded-lg bg-[var(--color-bg)] text-xl cursor-pointer flex items-center justify-center disabled:cursor-not-allowed"
            aria-label="Decrease quantity"
          >
            -
          </button>
          <input
            id="quantity-input"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
            className="text-xl font-semibold min-w-[60px] text-center border border-border rounded-lg p-2"
            aria-label="Product quantity"
            data-testid="quantity-input"
          />
          <button
            onClick={() => handleQuantityChange(quantity + 1)}
            className="w-10 h-10 border border-border rounded-lg bg-[var(--color-bg)] text-xl cursor-pointer flex items-center justify-center"
            aria-label="Increase quantity"
          >
            +
          </button>
        </div>
      </div>

      {/* Add to Cart Button */}
      <button
        onClick={handleAddToCart}
        disabled={!isAvailable}
        className={`w-full py-md border-none rounded-sm text-base font-semibold mb-2xl transition-colors ${
          isAvailable
            ? 'bg-primary text-[var(--color-text-on-primary)] cursor-pointer hover:bg-primary-hover'
            : 'bg-bg-tertiary text-text-light cursor-not-allowed'
        }`}
        data-testid="add-to-cart-button"
        aria-label={isAvailable ? `Add ${quantity} ${product.title} to cart` : 'Product unavailable'}
      >
        {!currentVariant ? 'No Variants Available' :
         !isAvailable ? 'Out of Stock' :
         `Add ${quantity} to Cart`}
      </button>

      {/* Availability Status */}
      {isAvailable && (
        <div className="mb-8" role="status" aria-live="polite">
          <span className="text-sm text-green-600">✓ In Stock</span>
        </div>
      )}
    </>
  );

  const descriptionSection = product.description ? (
    <div>
      <h2 className="text-xl font-semibold text-text-primary mb-4">
        Description
      </h2>
      <p className="text-text-secondary leading-relaxed text-base">
        {product.description}
      </p>
    </div>
  ) : null;

  const detailsSection = (
    <div className="mt-8">
      <h3 className="text-xl font-semibold text-text-primary mb-4">
        Product Details
      </h3>
      <div className="text-text-secondary text-sm">
        <p><strong>SKU:</strong> {currentVariant?.sku || 'N/A'}</p>
        {product.weight && <p><strong>Weight:</strong> {product.weight}g</p>}
        {product.material && <p><strong>Material:</strong> {product.material}</p>}
        <p><strong>Handle:</strong> {product.handle}</p>
      </div>
    </div>
  );

  const socialSection = showSocialShare ? (
    <div className="mt-8 pt-8 border-t border-border-light">
      <p className="text-sm text-text-secondary mb-3">Share</p>
      <div className="flex gap-4">
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-secondary hover:text-primary transition-colors text-sm no-underline"
          aria-label="Share on Facebook"
        >
          Facebook
        </a>
        <a
          href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(product.title)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-secondary hover:text-primary transition-colors text-sm no-underline"
          aria-label="Share on X"
        >
          X
        </a>
        <a
          href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}&media=${encodeURIComponent(product.images?.[0]?.url || '')}&description=${encodeURIComponent(product.title)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-secondary hover:text-primary transition-colors text-sm no-underline"
          aria-label="Share on Pinterest"
        >
          Pinterest
        </a>
      </div>
    </div>
  ) : null;

  // Tab content for the enterprise (`infoLayout === "tabs"`) layout.
  // Overview always carries the description + product details; the other
  // tabs read product.metadata with a graceful fallback so the strip is
  // data-driven for future enriched products without blocking on metadata
  // that doesn't exist yet.
  const productMeta = (product.metadata || {}) as Record<string, unknown>;
  const metaTabContent = (key: string): React.ReactNode => {
    const value = productMeta[key];
    if (Array.isArray(value)) {
      return (
        <ul className="list-disc pl-5 space-y-2">
          {value.map((item, i) => (
            <li key={i}>{String(item)}</li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'string' && value.trim()) {
      return <p>{value}</p>;
    }
    return <p className="text-text-light italic">Details available on request — contact our team.</p>;
  };
  const infoTabs: ProductInfoTab[] = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <>
          {descriptionSection}
          {detailsSection}
        </>
      ),
    },
    { id: 'deliverables', label: 'Deliverables', content: metaTabContent('deliverables') },
    { id: 'methodology', label: 'Methodology', content: metaTabContent('methodology') },
    { id: 'faq', label: 'FAQ', content: metaTabContent('faq') },
  ];

  return (
    <div className="max-w-6xl mx-auto p-8" role="main">
      {/* Breadcrumb */}
      {showBreadcrumbs && (
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm list-none p-0 m-0">
            <li>
              <button
                onClick={() => navigate('/')}
                className="bg-transparent border-none text-text-secondary cursor-pointer hover:text-primary transition-colors p-0"
              >
                Home
              </button>
            </li>
            <li className="text-text-light" aria-hidden="true">/</li>
            <li>
              <button
                onClick={() => navigate('/products')}
                className="bg-transparent border-none text-text-secondary cursor-pointer hover:text-primary transition-colors p-0"
              >
                Products
              </button>
            </li>
            <li className="text-text-light" aria-hidden="true">/</li>
            <li className="text-text-primary font-medium" aria-current="page">
              {product.title}
            </li>
          </ol>
        </nav>
      )}

      {isSidebarRight ? (
        <>
          {/* Enterprise sidebar-right: 2/3 gallery + 1/3 sticky buy box */}
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start"
            data-testid="pdp-top-region"
          >
            <div className="lg:col-span-2" data-testid="pdp-main-column">
              {imageGallery}
            </div>
            <div
              className="lg:col-span-1 lg:sticky lg:top-24"
              data-testid="pdp-sidebar"
            >
              {buyBox}
            </div>
          </div>

          {useInfoTabs ? (
            <ProductInfoTabs tabs={infoTabs} />
          ) : (
            <div className="mt-16 max-w-3xl">
              {descriptionSection}
              {detailsSection}
              {socialSection}
            </div>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          {imageGallery}

          {/* Product Information */}
          <div>
            {buyBox}
            {descriptionSection}
            {detailsSection}
            {socialSection}
          </div>
        </div>
      )}

      {/* Related Products */}
      {relatedConfig?.show && (
        <RelatedProducts
          product={product}
          title={relatedConfig.title || 'You may also like'}
          count={relatedConfig.count || 4}
        />
      )}
    </div>
  );
};

export default ProductDetailPage;
